<template>
  <div class="pdf-output-area mt-5">
    <div ref="pdfContent">
      <div v-if="schedule">
        <div
          v-for="(page, pageIndex) in paginatedSchedule"
          :key="pageIndex"
          class="page"
        >
          <div 
            class="list-container" 
            :class="{ 'page-break': pageIndex !== 0 }"
          >
            <div class="d-flex align-items-center">
              <div class="d-width-one-forth"></div>
              <div class="d-width-two-forth"><h1 class="mb-2 text-center"><span class="me-4">* *</span>業者別作業予定表<span class="ms-4">* *</span></h1></div>
              <div class="d-width-one-forth text-end">
                <span class="me-2">{{ formatDay(new Date()) }}</span><span>PAGE - {{ pageIndex + 1 }}/{{ paginatedSchedule.length }}</span>
              </div>
            </div>

            <div class="header mb-2">
              <div class="middle-title d-flex justify-content-between align-items-center">
                <div class="one-third-block">
                  <p class="mb-1">株式会社創英企画 御中</p>
                  <p class="mb-0">FAX 03-3370-8157</p>
                </div>
                <div class="one-third-block">
                  <p class="mb-0 text-center">作業予定日 {{ workPeriod }}</p>
                </div>
                <div class="one-third-block">
                  <p class="mb-1 text-center">{{ vendor.vendor_nm }}</p>
                  <p class="mb-1 text-center">{{ schedule.destination_address_01 }}</p>
                  <p class="mb-0 text-center">FAX {{ vendor.fax }}</p>
                </div>
              </div>
            </div>

            <!-- 業務内容のテーブル -->
            <table class="table monthly-detail-data">
              <thead>
                <tr>
                  <th class="month-data-header text-start">作業項目</th>
                  <th class="month-data-header text-start">ビル名</th>
                  <th class="month-data-header">作業予定日</th>
                  <th class="month-data-header">作業開始時間</th>
                  <th class="month-data-header">作業終了時間</th>
                  <th class="month-data-header">年度</th>
                  <th class="month-data-header text-start" colspan="2">発注金額</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(scheduleItem, scIndex) in page"
                  :key="scIndex"
                >
                  <td class="text-start">
                    {{ scheduleItem?.t_building_work_schedule_detail?.t_building_work_annual_schedule?.work_content }}
                  </td>
                  <td class="text-start">
                    {{ scheduleItem.t_building_work_schedule_detail.t_building_work_annual_schedule.m_building.building_nm }}
                  </td>
                  <td>{{ formatDay(scheduleItem.work_plan_dt) }}</td>
                  <td>{{ formatDateTime(scheduleItem.start_work_plan_time) }}</td>
                  <td>{{ formatDateTime(scheduleItem.end_work_plan_time) }}</td>
                  <td>{{ year }}</td>
                  <td class="text-start">
                    {{ formatCurrency(scheduleItem.t_building_work_schedule_detail.request_amount) }}
                  </td>
                  <td></td>
                </tr>

                <tr v-if="isLastPage(pageIndex)">
                  <td class="text-start" colspan="6"><span class="me-4">* *</span>業者合計<span class="ms-4">* *</span></td>
                  <td class="text-start">{{ calculateTotal(schedule.t_vendor_work_monthly_calendar_details) }}</td>
                  <td class="text-nowrap">
                    {{ schedule.t_vendor_work_monthly_calendar_details?.length }}件
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div v-else>請負業者予定表情報を読み込み中...</div>
    </div>
  </div>
</template>

<script>
import { ref, toRefs, computed, onMounted } from 'vue';
import { getVendor, getVendorMonthlyWorkScheduleDetails } from '@/api/axios';

export default {
  props: {
    // 値を受け取るためのpropsを定義
    vendorId: {
      type: [String, Number], // 受け取る値の型
      required: true, // 必須のpropsであることを示す
    },
    year: {
      type: [String, Number], // 受け取る値の型
      required: true, // 必須のpropsであることを示す
    },
    month: {
      type: [String, Number], // 受け取る値の型
      required: true, // 必須のpropsであることを示す
    }
  },
  setup(props) {
    const userId = ref('');
    const userName = ref('');
    const vendor = ref({});
    const schedule = ref({});

    const { vendorId } = toRefs(props);
    const { year } = toRefs(props);
    const { month } = toRefs(props);

    // 月の最初の日と最後の日を取得する関数
    const getFirstAndLastDay = () => {
      const firstDay = new Date(year.value, month.value - 1, 1); // 月の初日
      const lastDay = new Date(year.value, month.value, 0); // 月の末日

      const formatDate = (date) => {
        return date.toLocaleDateString('ja-JP', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).replace(/\//g, '/');
      };

      return {
        firstDay: formatDate(firstDay),
        lastDay: formatDate(lastDay)
      };
    };

    // 初日と末日を計算して取得
    const workPeriod = computed(() => {
      const { firstDay, lastDay } = getFirstAndLastDay();
      return `${firstDay} ~ ${lastDay}`;
    });

    const fetchWorkScheduleDatas = async () => {
      userId.value = localStorage.getItem('userId');
      userName.value = localStorage.getItem('userName');
      
      try {
        const response = await getVendor(vendorId.value);
        vendor.value = response.data;
      } catch (error) {
        console.error('業者情報データの取得に失敗しました:', error);
      }
      
      try {
        const response = await getVendorMonthlyWorkScheduleDetails(vendorId.value, year.value, month.value);
        schedule.value = response.data.vendor_work_schedule;
      } catch (error) {
        console.error('年間スケジュールの取得に失敗しました:', error);
      }
    };

    const splitIntoPages = (items, itemsPerPage) => {
      const result = [];
      for (let i = 0; i < items.length; i += itemsPerPage) {
        result.push(items.slice(i, i + itemsPerPage));
      }
      return result;
    };

    const paginatedSchedule = computed(() => {
      const details = schedule.value.t_vendor_work_monthly_calendar_details || [];
      console.log('あああああああああああああああああああああ')
      console.log(details)
      return splitIntoPages(details, 12);
    });

    const isLastPage = (pageIndex) => {
      return pageIndex === paginatedSchedule.value.length - 1;
    };
    
    // 日時フォーマット変換メソッド
    const formatDateTime = (dateTime) => {
      if (!dateTime) {
        return '';
      }
      const date = new Date(dateTime);
      return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
    };

    const formatDay = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${year.value}/${month.value}/${date.getDate()}`;  // 日付部分だけを取得
    };

    // Format currency
    const formatCurrency = (value) => {
      if (value) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
        }).format(value);
      }
      return '¥0';
    };

    // 合計を計算するメソッド
    const calculateTotal = (details) => {
      if (!details) return '¥0'; // 詳細がない場合は¥0を返す
      const total = details.reduce((sum, detail) => {
        // detail.bill_amountがあれば加算し、なければ0を加算
        return sum + (detail.t_building_work_schedule_detail?.request_amount || 0);
      }, 0);
      return formatCurrency(total); // フォーマットされた通貨として返す
    };

    onMounted(() => {
      fetchWorkScheduleDatas();
    });

    return {
      vendor,
      schedule,
      workPeriod,
      paginatedSchedule,
      isLastPage,
      formatCurrency,
      calculateTotal,
      formatDateTime,
      formatDay
    };
  }
};
</script>

<style scoped>
.list-container {
  width: 100%;
  margin: auto;
}

.d-width-two-forth{
  width: calc(100% / 2);
}

.d-width-one-forth{
  width: calc(100% / 4);
}

.header h2 {
  text-align: left;
  font-weight: bold;
}

.header p {
  text-align: left;
  margin-bottom: 20px;
}

.button-area {
  text-align: right;
  margin-bottom: 20px;
}

.button-area .btn {
  margin-left: 10px;
}

.text-center {
  text-align: center;
}

.monthly-detail-data {
  margin: 20px auto;
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 4px 8px;
  border-bottom: 1px solid #000;
  text-align: center;
  font-size: 14px;
}

.none-border-bottom td{
  border-bottom: none !important;
}

.monthly-detail-data th.month-data-header {
  background-color: #f0f8ff;
  font-weight: bold;
}

.page-break {
  page-break-before: always;
}
</style>
